import type { OperatingParameters } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import { ref } from "vue";
import type {
  HypotheticalFrequencyDataSet,
  HypotheticalFrequencyDataRow
} from ".";
import { doOneRow } from "./doOneRow";

export const doOneSet = (
  hypotheticalFrequencies: number[],
  scenarioCalculations: ScenarioCalculations,
  parameters: OperatingParameters,
  depth: number,
  suppressRecursion = false,
  envelope?: { minimumHz: number; maximumHz: number }
): HypotheticalFrequencyDataSet => {
  const rows: HypotheticalFrequencyDataRow[] = [];

  const hypotheticalParameters = ref<OperatingParameters>({
    ...parameters,
    __typename: "scenario_parameters"
  });

  const hypotheticalScenarioCalcs = useScenarioCalculations({
    ...scenarioCalculations.destructuredScenario,
    parameters: hypotheticalParameters
  });
  const aggregatePumpCalcs =
    hypotheticalScenarioCalcs.pumpCalculations.aggregate;
  const hypotheticalParameterValues = hypotheticalParameters.value!;

  for (let i = 0; i < hypotheticalFrequencies.length - 1; i++) {
    // prep data
    const previousRow = rows[i - 1];
    const previousChange = previousRow?.pressureDifference;
    const hypotheticalFrequencyHz = hypotheticalFrequencies[i];
    if (envelope && (hypotheticalFrequencyHz < envelope.minimumHz || hypotheticalFrequencyHz > envelope.maximumHz)) continue;
    hypotheticalParameterValues.pump_hz = hypotheticalFrequencyHz;
    const producedPressurePSIA =
      aggregatePumpCalcs.producedDischargePressurePSIA.value;
    const requiredPressurePSIA =
      aggregatePumpCalcs.requiredDischargePressurePSIA.value;

    // build the current row
    const currentRow = doOneRow(
      hypotheticalFrequencyHz,
      producedPressurePSIA,
      requiredPressurePSIA,
      depth,
      previousChange
    );

    rows.push(currentRow);

    const { status } = currentRow;

    if (status === "result-found") {
      // this is the value we are looking for
      return {
        status,
        rows,
        depth,
        targetRow: currentRow
      };
    }
    if (status === "threshold-found" && !suppressRecursion) {
      const nextMaxFrequency =
        i === 0 ? hypotheticalFrequencies[0] : hypotheticalFrequencies[i - 1];
      const nextMinFrequency =
        i === hypotheticalFrequencies.length - 1
          ? hypotheticalFrequencies[hypotheticalFrequencies.length - 1]
          : hypotheticalFrequencies[i + 1];

      const nextHypotheticalFrequencies = getNextHypotheticalFrequencies(
        nextMaxFrequency,
        nextMinFrequency
      );

      const nextDataSet = doOneSet(
        nextHypotheticalFrequencies,
        scenarioCalculations,
        parameters,
        depth + 1,
        false,
        envelope
      );

      rows.push(...nextDataSet.rows);

      return {
        ...nextDataSet,
        rows
      };
    }
  }
  return {
    rows,
    targetRow: rows[rows.length - 1],
    depth,
    status: "error: cannot find tolerance hz"
  };
};

const getNextHypotheticalFrequencies = (
  nextMaxFrequency: number,
  nextMinFrequency: number
) => {
  const stepSize = (nextMaxFrequency - nextMinFrequency) / 20;
  const NUM_STEPS = 20;
  const nextHypotheticalFrequencies = Array.from(
    { length: NUM_STEPS },
    (_, i) => i
  ).map(i => nextMaxFrequency - i * stepSize);

  return nextHypotheticalFrequencies;
};
